import { useState } from "react";
import { refundPolicyConsentText } from "../checkout";
import type { Offer } from "../types";
import { AsyncStatus, Button } from "../components";

const BOMANA_SHOWCASE = [
  {
    id: "web",
    src: "/brand/bomana-web-current.webp",
    label: "战术 Web",
    detail: "航向、地图、解算与任务信息",
    alt: "Bomana Web 实机界面，包含航向带、战术地图、武器解算与任务信息",
    fit: "cover-top",
  },
  {
    id: "launcher",
    src: "/brand/bomana-launcher-current.webp",
    label: "在线 Launcher",
    detail: "连接 Bridge、选择版本并进入 App",
    alt: "Bomana Online Launcher 实机界面，包含 Bridge 状态、版本选择与 Enhanced 授权",
    fit: "contain",
  },
  {
    id: "mobile",
    src: "/brand/bomana-mobile-current.webp",
    label: "手机战术页",
    detail: "航向、地图、目标与武器控制",
    alt: "Bomana 手机实机界面，包含航向带、战术地图、目标与武器控制",
    fit: "contain",
  },
  {
    id: "pip",
    src: "/brand/bomana-pip-current.webp",
    label: "置顶战术窗",
    detail: "紧凑航向、投放窗口与缩略图",
    alt: "Bomana 置顶战术窗实机界面，包含航向带、投放窗口与战术缩略图",
    fit: "contain-wide",
  },
] as const;

const BOMANA_FEATURES = [
  ["MAP", "战术地图", "战区格子、机场模块、聊天标记与倒计时"],
  ["WPN", "武器解算", "常规、高阻、卫星制导、滑翔武器与导弹投放参考"],
  ["NAV", "导航与余量", "航向、距离、燃油、返航和 IAS / Mach 风险"],
  ["Y66", "标定与高程", "Y66 标定、本机离线高程与地形辅助"],
  ["SYNC", "多屏协同", "Web、手机和置顶战术窗共享当前任务状态"],
] as const;

interface BomanaStorefrontProps {
  salesPaused: boolean;
  offers: Offer[];
  catalogLoaded: boolean;
  catalogError: string;
  acceptedRefundPolicies: Record<string, boolean>;
  busyProduct: string;
  purchaseError: string;
  signedIn: boolean;
  onBuy: (productId: string) => void;
  onRefundPolicyChange: (productId: string, accepted: boolean) => void;
  onRetryCatalog: () => void;
}

export function BomanaStorefront({
  salesPaused,
  offers,
  catalogLoaded,
  catalogError,
  acceptedRefundPolicies,
  busyProduct,
  purchaseError,
  signedIn,
  onBuy,
  onRefundPolicyChange,
  onRetryCatalog,
}: BomanaStorefrontProps) {
  const [activeShotId, setActiveShotId] = useState<(typeof BOMANA_SHOWCASE)[number]["id"]>("web");
  const activeShot =
    BOMANA_SHOWCASE.find((shot) => shot.id === activeShotId) ??
    BOMANA_SHOWCASE[0];

  return (
    <section className="storefront-layout" aria-labelledby="bomana-storefront-title">
      <div className="storefront-overview">
        <header className="storefront-hero">
          <div className="storefront-lockup">
            <img alt="" aria-hidden="true" height="72" src="/brand/bomana-app.png" width="72" />
            <div>
              <span className="eyebrow">Bomana · Enhanced</span>
              <h1 id="bomana-storefront-title">
                分体式浏览器计算，助你放心使用
              </h1>
            </div>
          </div>
          <p>
            运行只读 Bridge 后直接从浏览器使用。电脑、手机和置顶战术窗共享当前任务状态。
          </p>
          <div className="storefront-summary" aria-label="产品摘要">
            <span>Web · 手机 · 置顶窗</span>
            <span>只读 ExtUI</span>
            <span>{salesPaused ? "免费版可用" : "付款后自动开通"}</span>
          </div>
        </header>

        <div className="storefront-gallery">
          <a
            aria-label={`查看${activeShot.label}原图`}
            className="storefront-gallery-main"
            href={activeShot.src}
            rel="noreferrer"
            target="_blank"
          >
            <div className={`storefront-shot-stage ${activeShot.fit}`}>
              <img
                alt={activeShot.alt}
                decoding="async"
                fetchPriority="high"
                key={activeShot.src}
                src={activeShot.src}
              />
            </div>
          </a>
          <div className="storefront-gallery-caption">
            <span>
              <strong>{activeShot.label}</strong>
              <small>{activeShot.detail}</small>
            </span>
            <span aria-hidden="true">点击图片查看原图 ↗</span>
          </div>
          <div className="storefront-gallery-tabs" aria-label="选择产品截图">
            {BOMANA_SHOWCASE.map((shot) => (
              <button
                aria-pressed={shot.id === activeShot.id}
                className={shot.id === activeShot.id ? "active" : ""}
                key={shot.id}
                onClick={() => setActiveShotId(shot.id)}
                type="button"
              >
                <span className={`storefront-gallery-thumb ${shot.fit}`}>
                  <img alt="" aria-hidden="true" loading="lazy" src={shot.src} />
                </span>
                <span>
                  <strong>{shot.label}</strong>
                  <small>{shot.detail}</small>
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <aside className="storefront-purchase" aria-labelledby="purchase-title">
        {salesPaused ? (
          <>
            <header className="storefront-purchase-heading">
              <span className="eyebrow">支付公告</span>
              <h2 id="purchase-title">在线支付暂时不可用</h2>
              <p>证照办理期间，首页暂停展示和销售付费授权。</p>
            </header>

            <div className="storefront-payment-notice" role="status">
              <span aria-hidden="true" className="storefront-payment-notice-mark">
                办
              </span>
              <div>
                <strong>请先使用免费版</strong>
                <p>
                  由于支付宝商家备案要求，在线支付暂时不可用。请先使用免费版，本站正在火速申办个体工商户等证照。
                </p>
              </div>
              <a
                className="button"
                href="https://bomana.ruikang.wang/"
                rel="noreferrer"
                target="_blank"
              >
                前往 Bomana 免费版
              </a>
            </div>

            <div className="storefront-purchase-footnote">
              <strong>证照准备就绪后，这里会恢复在线支付</strong>
              <span>Bomana 是独立第三方工具，使用前请自行确认当前游戏规则。</span>
            </div>
          </>
        ) : (
          <>
            <header className="storefront-purchase-heading">
              <span className="eyebrow">Enhanced 授权</span>
              <h2 id="purchase-title">选择期限</h2>
              <p>两种方案功能相同，只区别试用期限与购买规则。</p>
            </header>

            <div className="storefront-offers">
              {offers.length ? (
                offers.map((offer) => (
                  <StorefrontOffer
                    accepted={acceptedRefundPolicies[offer.productId] === true}
                    busy={busyProduct === offer.productId}
                    key={offer.productId}
                    offer={offer}
                    onBuy={() => onBuy(offer.productId)}
                    onRefundPolicyChange={(accepted) =>
                      onRefundPolicyChange(offer.productId, accepted)
                    }
                  />
                ))
              ) : (
                <StorefrontCatalogState
                  catalogError={catalogError}
                  catalogLoaded={catalogLoaded}
                  onRetry={onRetryCatalog}
                />
              )}
            </div>

            {purchaseError ? (
              <AsyncStatus compact tone="error">
                {purchaseError}
              </AsyncStatus>
            ) : null}

            <div className="storefront-purchase-footnote">
              <strong>{signedIn ? "授权开通到当前账号" : "购买时先登录或创建账号"}</strong>
              <span>Bomana 是独立第三方工具，使用前请自行确认当前游戏规则。</span>
            </div>
          </>
        )}
      </aside>

      <div className="storefront-features" aria-label="Bomana Enhanced 核心功能">
        {BOMANA_FEATURES.map(([code, title, detail]) => (
          <article key={code}>
            <span>{code}</span>
            <div>
              <h2>{title}</h2>
              <p>{detail}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function StorefrontOffer({
  offer,
  accepted,
  busy,
  onBuy,
  onRefundPolicyChange,
}: {
  offer: Offer;
  accepted: boolean;
  busy: boolean;
  onBuy: () => void;
  onRefundPolicyChange: (accepted: boolean) => void;
}) {
  const refundConsent = refundPolicyConsentText(offer);
  const days = Math.max(1, Math.round(offer.durationSeconds / 86_400));
  const trial = offer.code.includes("trial");
  const currency = offer.currency === "CNY" ? "¥" : offer.currency;

  return (
    <article className={`storefront-offer ${trial ? "trial" : "annual"}`}>
      <header>
        <span>{trial ? "完整功能试用" : "年度授权"}</span>
        <small>正在售卖</small>
      </header>
      <h3>{offer.name}</h3>
      <div className="storefront-offer-price">
        <strong>
          {currency}
          {(offer.amountFen / 100).toFixed(2)}
        </strong>
        {offer.originalAmountFen !== null ? (
          <small>
            原价 <del>{currency}{(offer.originalAmountFen / 100).toFixed(2)}</del>
          </small>
        ) : null}
      </div>
      <div className="storefront-offer-facts">
        <span>{days} 天</span>
        <span>{offer.maxDevices} 台设备</span>
        {offer.purchaseLimit !== null ? <span>限购 {offer.purchaseLimit} 次</span> : null}
      </div>
      {refundConsent ? (
        <label className="purchase-refund-consent">
          <input
            checked={accepted}
            onChange={(event) => onRefundPolicyChange(event.target.checked)}
            type="checkbox"
          />
          <span>{refundConsent}</span>
        </label>
      ) : (
        <small className="storefront-offer-refund">
          168 小时内按有效使用次数分档退款。
        </small>
      )}
      <Button disabled={busy} onClick={onBuy}>
        {busy ? "创建订单中…" : trial ? "购买试用" : "购买年度授权"}
      </Button>
    </article>
  );
}

function StorefrontCatalogState({
  catalogLoaded,
  catalogError,
  onRetry,
}: {
  catalogLoaded: boolean;
  catalogError: string;
  onRetry: () => void;
}) {
  if (catalogError) {
    return (
      <AsyncStatus
        action={
          <Button onClick={onRetry} type="button" variant="secondary">
            重试
          </Button>
        }
        tone="error"
      >
        {catalogError}
      </AsyncStatus>
    );
  }
  return (
    <div className="storefront-catalog-state" role="status">
      <img alt="" aria-hidden="true" src="/brand/cheems-burger.webp" />
      <div>
        <strong>{catalogLoaded ? "暂无在售授权" : "正在读取价格…"}</strong>
        <span>{catalogLoaded ? "新方案上架后会显示在这里。" : "请稍候。"}</span>
      </div>
    </div>
  );
}
